import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

// Fallbacks for self-hosted mode
const ENV_MERCHANT_ID = process.env.REACT_APP_MERCHANT_ID;
const ENV_SHOP_NAME = process.env.REACT_APP_SHOP_NAME;

const ShopContext = createContext(null);

export const useShop = () => {
    const context = useContext(ShopContext);
    if (!context) {
        throw new Error('useShop must be used within a ShopProvider');
    }
    return context;
};

/**
 * Extract subdomain from hostname
 * Examples:
 * - glamnglow.instagshop.com -> glamnglow
 * - localhost:3001 -> null (development)
 * - merchant-hosting.preview.emergentagent.com -> null (Emergent preview)
 */
const getSubdomain = () => {
    const hostname = window.location.hostname;
    
    // Skip for localhost
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return null;
    }
    
    // Skip for preview/hosting environments (Emergent, Vercel, Netlify, Cloudflare Pages)
    if (hostname.includes('.preview.emergentagent.com') || 
        hostname.includes('.vercel.app') || 
        hostname.includes('.netlify.app') ||
        hostname.includes('.pages.dev')) {
        return null;
    }
    
    // For production: extract subdomain from hostname
    // Expected format: subdomain.instagshop.com or subdomain.yourdomain.com
    const parts = hostname.split('.');
    
    // Must have at least 3 parts (subdomain.domain.tld)
    if (parts.length >= 3) {
        const subdomain = parts[0];
        // Skip www
        if (subdomain !== 'www') {
            return subdomain;
        }
    }
    
    return null;
};

/**
 * Get shop identifier from URL query param (for testing)
 * Example: ?shop=glamnglow
 */
const getShopFromQuery = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get('shop');
};

export const ShopProvider = ({ children }) => {
    const [shopData, setShopData] = useState(null);
    const [merchantId, setMerchantId] = useState(ENV_MERCHANT_ID || null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [mode, setMode] = useState('self-hosted'); // 'hosted' or 'self-hosted'

    useEffect(() => {
        const initializeShop = async () => {
            setLoading(true);
            setError(null);

            // Priority 1: Check for subdomain (production hosted mode)
            const subdomain = getSubdomain();
            
            // Priority 2: Check for ?shop= query param (testing)
            const shopQuery = getShopFromQuery();
            
            // Priority 3: Use env variable (self-hosted mode)
            const envMerchantId = ENV_MERCHANT_ID;

            try {
                if (subdomain) {
                    // Hosted mode via subdomain
                    console.log(`[ShopContext] Hosted mode - subdomain: ${subdomain}`);
                    const response = await axios.get(`${API_URL}/hosted/${subdomain}`);
                    setShopData(response.data);
                    setMerchantId(response.data.owner_id);
                    setMode('hosted');
                } else if (shopQuery) {
                    // Testing mode via query param
                    console.log(`[ShopContext] Testing mode - shop query: ${shopQuery}`);
                    const response = await axios.get(`${API_URL}/hosted/${shopQuery}`);
                    setShopData(response.data);
                    setMerchantId(response.data.owner_id);
                    setMode('hosted');
                } else if (envMerchantId) {
                    // Self-hosted mode via env variable
                    console.log(`[ShopContext] Self-hosted mode - merchant: ${envMerchantId}`);
                    try {
                        const response = await axios.get(`${API_URL}/store/${envMerchantId}/settings`);
                        setShopData({
                            owner_id: envMerchantId,
                            shop_name: response.data.shop_name || ENV_SHOP_NAME || 'Shop',
                            shop_description: response.data.shop_description || '',
                            logo_url: response.data.logo_url || '',
                            subdomain: null
                        });
                    } catch (e) {
                        // Settings not found, use defaults
                        setShopData({
                            owner_id: envMerchantId,
                            shop_name: ENV_SHOP_NAME || 'Shop',
                            shop_description: '',
                            logo_url: '',
                            subdomain: null
                        });
                    }
                    setMerchantId(envMerchantId);
                    setMode('self-hosted');
                } else {
                    // No configuration found
                    setError('Shop not configured. Please provide REACT_APP_MERCHANT_ID or use a subdomain.');
                }
            } catch (err) {
                console.error('[ShopContext] Error loading shop:', err);
                if (err.response?.status === 404) {
                    setError('Shop not found. Please check the URL or configuration.');
                } else {
                    setError('Failed to load shop data. Please try again later.');
                }
            } finally {
                setLoading(false);
            }
        };

        initializeShop();
    }, []);

    const sub = shopData?.subdomain || null;
    const shopUrl = shopData?.hosted_url || (sub ? `https://${sub}.instagshop.com` : null) || (typeof window !== 'undefined' ? window.location.origin : '');

    const value = {
        shopData,
        merchantId,
        loading,
        error,
        mode,
        shopName: shopData?.shop_name || ENV_SHOP_NAME || 'Shop',
        shopDescription: shopData?.shop_description || '',
        logoUrl: shopData?.logo_url || '',
        subdomain: sub,
        shopUrl: shopUrl || ''
    };

    return (
        <ShopContext.Provider value={value}>
            {children}
        </ShopContext.Provider>
    );
};
